import {
	CacheSource,
	EventResultType,
	RequestEvent,
	RequestMethod,
	RequestMiddleware,
} from "../ApiTypes";

import * as StorageUtils from "../../util/StorageUtils";
import { StorageKey } from "../../util/StorageUtils";
import * as Flags from "../../Flags";
import * as StringUtils from "../../util/StringUtils";

export interface CacheMiddlewareOptions {
	defaultExpiry?: number;
	predicate?: () => boolean;
}

export const DEFAULT_CACHE_EXPIRE = 60 * 1000 * 15;

const generateStorageKey = (
	key: string,
	cache: boolean | number,
	options: CacheMiddlewareOptions,
): StorageKey<any> => {
	const expiry =
		typeof cache === "number"
			? cache
			: options.defaultExpiry || DEFAULT_CACHE_EXPIRE;

	return {
		id: `REQUEST[${key}]`,
		defaultValue: null,
		options: {
			expiry: expiry,
			session: true,
		},
	};
};

const CacheMiddleware = (
	options: CacheMiddlewareOptions = {},
): RequestMiddleware => {
	return {
		[RequestEvent.Success]: (context) => {
			if (context.method !== RequestMethod.Get) return;

			const { cache } = context.computedConfig.options || {};
			const shouldCache = !options.predicate || options.predicate();

			if (cache && shouldCache) {
				const storageKey = generateStorageKey(context.key, cache, options);
				if (storageKey) {
					StorageUtils.set(storageKey, context.response);
				}
			}
		},
		[RequestEvent.BeforeSend]: (context) => {
			if (context.method !== RequestMethod.Get) return;

			const { cache } = context.computedConfig.options || {};
			const shouldCache = !options.predicate || options.predicate();

			if (cache && shouldCache) {
				const storageKey = generateStorageKey(context.key, cache, options);
				if (cache) {
					const entry = StorageUtils.entry(storageKey);
					if (entry) {
						context.stats.cached = {
							is: true,
							by: "local",
						};

						context.cacheInfo.source = CacheSource.Local;
						context.cacheInfo.cached = true;

						return {
							type: EventResultType.Respond,
							response: entry.value,
						};
					}
				}
			}
			if (cache === false || Flags.DISABLE_API_CACHE.is()) {
				context.updateQuery({
					_bust: StringUtils.uuid(6),
				});
				context.updateHeaders({
					"Brightmile-Cache": "disabled",
					Pragma: "no-cache, no-store, must-revalidate",
					"Cache-Control": "no-cache, no-store, must-revalidate",
				});
			}
		},
	};
};

export default CacheMiddleware;
